import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useDistributorsContext } from "../../../Contexts/distributorsContext";
import { useFetchAllProductSettings } from "../hooks/useFetchAllProductSettings";
import { useFetchProductSettingsSkuOverrides } from "../hooks/useFetchProductSettingsSkuOverrides";
import { useFetchDistributorFulfillment } from "../hooks/useFetchDistributorFulfillment";
import { useFetchFulfillmentMethodMappings } from "../../ProductSettings/hooks/useFetchFulfillmentMethodMappings";
import { useFetchInternalSkusByDistributor, PutInternalSku } from "../hooks/useFetchInternalSkusByDistributor";

export const DistributorSettingsContext = React.createContext(null);

DistributorSettingsContextProvider.propTypes = {
    children: PropTypes.node,
}

export function DistributorSettingsContextProvider({ children }) {
    const { distributorId } = useParams();
    const distributorIdNum = Number(distributorId);
    const { distributors, isReady: areDistributorsReady } = useDistributorsContext();
    const selectedDistributor = distributors?.find(distributor => distributor.distributorId === distributorIdNum);
    const customerType = selectedDistributor?.type;

    const allProductSettings = useFetchAllProductSettings(distributorIdNum);
    const productSettingsSkuOverrides = useFetchProductSettingsSkuOverrides(distributorIdNum);
    const distributorFulfillment = useFetchDistributorFulfillment(distributorIdNum);
    const fulfillmentMethodMappings = useFetchFulfillmentMethodMappings();
    const internalSkus = useFetchInternalSkusByDistributor(distributorIdNum);

    const [dataLoaded, setDataLoaded] = useState(false);
    const [unfilteredSkuTable, setUnfilteredSkuTable] = useState([]);
    const [skuTable, setSkuTable] = useState();
    const [searchString, setSearchString] = useState("");
    const [selected, setSelected] = useState(new Set());
    const [inMarketDateError, setInMarketDateError] = useState(null);

    const showInMarketDateError = (message) => setInMarketDateError(message);
    const clearInMarketDateError = () => setInMarketDateError(null);

    const getPossibleSkuFulfillmentTypes = (customerType, offeringStatus) => {
        const relevantMappings = fulfillmentMethodMappings.filter(mapping =>
            mapping.customerType === customerType && mapping.offeringStatus === offeringStatus
        );

        const skuFulfillmentTypes = relevantMappings.map(mapping => ({
            id: mapping.fulfillmentType,
            description: mapping.fulfillmentTypeDescription,
            isDefault: mapping.isDefault
        }));

        return { customerType, skuFulfillmentTypes };
    };

    const isChanged = (newRow, oldRow) => {
        return (oldRow.palletQuantity !== newRow.palletQuantity ||
            oldRow.palletDivisor !== newRow.palletDivisor ||
            oldRow.unitWeight !== newRow.unitWeight ||
            oldRow.skuOfferingStatus?.id !== newRow.skuOfferingStatus?.id ||
            oldRow.skuFulfillmentType?.id !== newRow.skuFulfillmentType?.id ||
            oldRow.iIndirectDistributorId !== newRow.iIndirectDistributorId ||
            oldRow.inMarketDateOverride !== newRow.inMarketDateOverride ||
            oldRow.endInMarketDateOverride !== newRow.endInMarketDateOverride);
    };

    const isRowSelected = (row) => selected.has(row?.id) ?? false;

    const onRowSelectClick = (row, isChecked) => {
        const updated = new Set(selected);
        if (isChecked) {
            updated.add(row.id);
        } else {
            updated.delete(row.id);
        }
        setSelected(updated);
    };

    const selectAllLegalRows = () => {
        const allIds = skuTable.filter(row => row.legal !== false).map(row => row.id);
        setSelected(new Set(allIds));
    };

    const removeSelectedRows = () => setSelected(new Set());

    const loadSkuTableSettings = (allProductSettings, skuOverrides, distributorFulfillment, internalSkus) => {
        if (!allProductSettings || !skuOverrides || !distributorFulfillment || !internalSkus) {
            return null;
        }

        return allProductSettings.map(settings => {
            const override = skuOverrides.find(o => o.itemId === settings.itemId);
            const fulfillment = distributorFulfillment.find(f => f.product?.itemId === settings.itemId);
            const internalSkuMatch = internalSkus.find(i => i.sku === settings.sku);

            return {
                id: settings.sku,
                distributorId: distributorIdNum,
                product: {
                    itemId: settings.itemId,
                    sku: settings.sku,
                    description: settings.description,
                    brandFamily: settings.brandFamily,
                    brand: settings.brand,
                    category: settings.category,
                },
                seasonal: settings.seasonal,
                legal: override?.legal ?? true,
                palletQuantity: override?.palletQuantity ?? settings.palletQuantity,
                palletDivisor: override?.palletDivisor ?? settings.palletDivisor,
                unitWeight: override?.unitWeight ?? settings.unitWeight,
                skuOfferingStatus: fulfillment?.skuOfferingStatus,
                skuFulfillmentType: fulfillment?.skuFulfillmentType,
                indirectDistributorName: fulfillment?.indirectDistributorName,
                indirectDistributorSapId: fulfillment?.indirectDistributorSapId,
                iIndirectDistributorId: fulfillment?.distributorSkuFulfillmentType?.iIndirectDistributorId,
                inMarketDateDefault: fulfillment?.inMarketDateDefault,
                inMarketDateOverride: fulfillment?.inMarketDateOverride,
                inMarketDateIsDefault: fulfillment?.inMarketDateIsDefault,
                endInMarketDateDefault: fulfillment?.endInMarketDateDefault,
                endInMarketDateOverride: fulfillment?.endInMarketDateOverride,
                endInMarketDateIsDefault: fulfillment?.endInMarketDateIsDefault,
                paused: fulfillment?.paused,
                customerPartNumber: internalSkuMatch?.internalSku ?? '',
            };
        });
    };

    const searchFilter = (skuTable, searchString) => {
        if (!searchString || searchString?.trim() === '') {
            return skuTable;
        }

        let search = searchString.toLowerCase().trim();
        return skuTable.filter((row) =>
            row.product?.sku?.toLowerCase().includes(search)
            || row.product?.description?.toLowerCase().includes(search)
            || (search === 'legal' && row.legal)
            || (search === 'illegal' && !row.legal)
            || (row.skuFulfillmentType?.description?.toLowerCase().includes(search) ?? 'removed'.includes(search))
            || (row.skuOfferingStatus?.description?.toLowerCase().includes(search) ?? 'not offered'.includes(search)));
    };

    const loadPreparedTable = (skuMap, searchString) => {
        skuMap = searchFilter(skuMap, searchString);
        return [...skuMap].sort((a, b) => a.product.sku.localeCompare(b.product.sku));
    };

    const updateSkuTable = async (updatedRows) => {
        const updatedTable = unfilteredSkuTable.map((row) => {
            const updatedRow = updatedRows.find(updated => updated.id === row.id);
            return updatedRow ?? row;
        });

        await setUnfilteredSkuTable(updatedTable);
        setSkuTable(loadPreparedTable(updatedTable, searchString));
    };

    const saveCustomerPartNumber = async (row, value) => {
        await PutInternalSku(distributorIdNum, row.product.itemId, value);
        await updateSkuTable([{ ...row, customerPartNumber: value }]);
    };

    useEffect(() => {
        if (unfilteredSkuTable.length > 0) {
            setSkuTable(loadPreparedTable(unfilteredSkuTable, searchString));
            setDataLoaded(true);
        } else {
            const isAllDataReady = allProductSettings && productSettingsSkuOverrides && distributorFulfillment && internalSkus
                && fulfillmentMethodMappings.length > 0 && areDistributorsReady && selectedDistributor;

            if (isAllDataReady) {
                const table = loadSkuTableSettings(allProductSettings, productSettingsSkuOverrides, distributorFulfillment, internalSkus);
                setUnfilteredSkuTable(table);
                setSkuTable(loadPreparedTable(table, searchString));
            }

            setDataLoaded(isAllDataReady);
        }
    }, [allProductSettings, productSettingsSkuOverrides, distributorFulfillment, internalSkus, fulfillmentMethodMappings,
        areDistributorsReady, selectedDistributor, unfilteredSkuTable, searchString]);

    return (<DistributorSettingsContext.Provider value={{
        dataLoaded,
        selectedDistributor,
        customerType,
        skuTable,
        unfilteredSkuTable,
        updateSkuTable,
        saveCustomerPartNumber,
        getPossibleSkuFulfillmentTypes,
        fulfillmentMethodMappings,
        isChanged,
        searchString,
        setSearchString,
        onRowSelectClick,
        selected,
        isRowSelected,
        selectAllLegalRows,
        removeSelectedRows,
        inMarketDateError,
        showInMarketDateError,
        clearInMarketDateError,
    }}>{children}</DistributorSettingsContext.Provider>)
}

export function useDistributorSettingsContext() {
    const context = useContext(DistributorSettingsContext);

    if (!context) throw new Error("useDistributorSettingsContext must be used within a DistributorSettingsContextProvider. Wrap a parent component in <DistributorSettingsContextProvider> to fix this error.");
    return context;
}
